import React from 'react';
import PageMeta from '../common/PageMeta';
import { BuilderProvider, useBuilderStore } from '../../store/checkoutBuilder/BuilderContext';
import LegacyStage from './Canvas/LegacyStage';
import Inspector from './Inspector/Inspector';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { REGISTRY } from './registry';
import CheckoutBuilderErrorBoundary from './ErrorBoundary';
import ColorPicker from '../common/ColorPicker';
import { storageService } from '../../services/storageService';
import Modal from '../common/Modal';
import api from '../../services/api';
import Alert from '../ui/alert/Alert';

interface ToolbarProps {
  checkoutId?: string;
  productId?: string;
  offerId?: string;
  checkoutName?: string;
  globalSettings: {
    fontFamily: string;
    backgroundColor: string;
    backgroundImage: string;
    fixedBackground: boolean;
    repeatBackground: boolean;
    expandBackground: boolean;
  };
}

const Toolbar: React.FC<ToolbarProps> = ({ checkoutId, productId, offerId, checkoutName, globalSettings }) => {
  const { state, setBreakpoint, exportDocument, importDocument, addComponent, markSaved, deleteNode, replaceDocument } = useBuilderStore();
  const { ui, hasUnsavedChanges } = state;
  const [checkoutTitle, setCheckoutTitle] = React.useState(checkoutName || 'Nome do Checkout');
  const [isImportModalOpen, setIsImportModalOpen] = React.useState(false);
  const [importText, setImportText] = React.useState('');

  // Atualizar o título quando checkoutName mudar
  React.useEffect(() => {
    if (checkoutName) {
      setCheckoutTitle(checkoutName);
    }
  }, [checkoutName]);
  const [isSaving, setIsSaving] = React.useState(false);
  const [showCopyModal, setShowCopyModal] = React.useState(false);
  const [showExportModal, setShowExportModal] = React.useState(false);
  const [alertInfo, setAlertInfo] = React.useState<{
    show: boolean;
    type: 'success' | 'error' | 'warning' | 'info';
    title: string;
    message: string;
  } | null>(null);

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCheckoutTitle(e.target.value);
    // Qualquer mudança no título será detectada pelo sistema global
    // Por enquanto, vamos simular uma mudança adicionando uma linha invisível
    // Em uma implementação real, isso seria salvo no documento
  };

  // Função para converter data URL para File
  const dataURLtoFile = (dataurl: string, filename: string): File => {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)![1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  };

  // Função para encontrar APENAS imagens base64/blob (não URLs do S3)
  const findBase64Images = (document: any): Array<{ nodeId: string; propName: string; dataUrl: string }> => {
    const base64Images: Array<{ nodeId: string; propName: string; dataUrl: string }> = [];

    const scanNode = (nodeId: string, node: any) => {
      if (node.type === 'component' && node.props) {
        // Procurar por propriedades de imagem que contenham data: ou blob: (APENAS essas precisam de upload)
        for (const [propName, propValue] of Object.entries(node.props)) {
          if (typeof propValue === 'string') {
            // ✅ PRECISA UPLOAD: data:image/ ou blob:
            if (propValue.startsWith('data:image/') || propValue.startsWith('blob:')) {
              base64Images.push({
                nodeId,
                propName,
                dataUrl: propValue
              });
            }
            // ❌ NÃO PRECISA UPLOAD: URLs do S3, HTTP, HTTPS
            // Essas já estão no S3 ou são URLs externas válidas
          }
        }
      }
    };

    // Escanear todos os nodes do documento
    Object.entries(document.nodes).forEach(([nodeId, node]) => {
      scanNode(nodeId, node);
    });

    return base64Images;
  };

  // Função para fazer upload de uma imagem e retornar a URL
  const uploadImage = async (dataUrl: string, index: number): Promise<string> => {
    try {
      // Converter data URL para File
      const filename = `checkout-image-${Date.now()}-${index}.png`;
      const file = dataURLtoFile(dataUrl, filename);

      // Fazer upload para S3
      const result = await storageService.uploadFile(file, 'checkout-images', filename);

      if (result.success && result.url) {
        return result.url;
      } else {
        throw new Error(result.error || 'Erro no upload');
      }
    } catch (error) {
      console.error('Erro ao fazer upload da imagem:', error);
      throw error;
    }
  };

  const handleSave = async (checkoutId?: string) => {
    setIsSaving(true);
    try {
      // Validar productId (obrigatório apenas para CRIAR novo checkout)
      if (!productId && !checkoutId) {
        setAlertInfo({
          show: true,
          type: 'error',
          title: 'Erro de validação',
          message: 'ProductId é obrigatório para criar um novo checkout'
        });
        setTimeout(() => setAlertInfo(null), 5000);
        setIsSaving(false);
        return;
      }

      // Log para debug
      console.log('💾 Salvando checkout:', { checkoutId, productId, offerId });

      // 1. Criar uma cópia do documento para processamento
      const documentCopy = JSON.parse(JSON.stringify(state.document));

      // 2. Encontrar APENAS imagens base64/blob que precisam de upload
      const base64Images = findBase64Images({ nodes: documentCopy.nodes });

      if (base64Images.length > 0) {
        console.log(`🖼️ Encontradas ${base64Images.length} nova(s) imagem(ns) para upload ao S3...`);

        // 3. Fazer upload apenas das imagens novas (base64/blob)
        for (let i = 0; i < base64Images.length; i++) {
          const imageInfo = base64Images[i];
          console.log(`📤 Upload ${i + 1}/${base64Images.length}: Enviando nova imagem...`);

          try {
            const uploadedUrl = await uploadImage(imageInfo.dataUrl, i);

            // Substituir a URL base64 pela URL do S3 no documento
            documentCopy.nodes[imageInfo.nodeId].props[imageInfo.propName] = uploadedUrl;

            console.log(`✅ Imagem ${i + 1} salva no S3:`, uploadedUrl);
          } catch (uploadError) {
            console.error(`❌ Erro no upload da imagem ${i + 1}:`, uploadError);
            throw new Error(`Erro no upload da imagem ${i + 1}: ${uploadError instanceof Error ? uploadError.message : 'Erro desconhecido'}`);
          }
        }

        console.log('🎉 Todas as novas imagens foram enviadas para o S3!');
      } else {
        console.log('ℹ️ Nenhuma imagem nova encontrada (URLs do S3 mantidas)');
      }

      // 4. Criar o novo formato de dados para salvar (com URLs do S3)
      const saveData: any = {
        name: checkoutTitle,
        configurations: {
          desktop: {
            document: {
              root: documentCopy.root,
              nodes: Object.fromEntries(
                Object.entries(documentCopy.nodes).filter(([key]) =>
                  !key.startsWith('mobile-')
                )
              )
            },
            globalSettings: {
              fontFamily: globalSettings.fontFamily,
              backgroundColor: globalSettings.backgroundColor,
              backgroundImage: globalSettings.backgroundImage,
              fixedBackground: globalSettings.fixedBackground,
              repeatBackground: globalSettings.repeatBackground,
              expandBackground: globalSettings.expandBackground,
              checkoutLayout: (documentCopy.meta as any)?.checkoutLayout || 'two-columns'
            }
          },
          mobile: {
            document: {
              mobileRoot: (documentCopy as any).mobileRoot || [],
              nodes: Object.fromEntries(
                Object.entries(documentCopy.nodes).filter(([key]) =>
                  key.startsWith('mobile-')
                )
              )
            },
            globalSettings: {
              fontFamily: globalSettings.fontFamily,
              backgroundColor: globalSettings.backgroundColor,
              backgroundImage: globalSettings.backgroundImage,
              fixedBackground: globalSettings.fixedBackground,
              repeatBackground: globalSettings.repeatBackground,
              expandBackground: globalSettings.expandBackground,
              checkoutLayout: 'one-column'
            }
          }
        }
      };

      // Adicionar productId e offerId apenas se existirem
      if (productId) {
        saveData.productId = productId;
      }
      if (offerId !== undefined) {
        saveData.offerId = offerId;
      }

      // 5. Salvar no backend usando o padrão do projeto
      console.log('💾 Salvando checkout no backend...');
      let response;

      if (checkoutId) {
        // Atualizar checkout existente
        response = await api.put(`/api/checkout-builder/${checkoutId}`, saveData);
      } else {
        // Criar novo checkout
        response = await api.post('/api/checkout-builder', saveData);
      }

      const result = response.data;
      console.log('Checkout salvo com sucesso:', result);

      // Marcar como salvo para remover "alterações não salvas"
      markSaved();

      // Mensagem personalizada baseada no que foi processado
      const message = 'Checkout salvo com sucesso';

      setAlertInfo({
        show: true,
        type: 'success',
        title: 'Tudo certo!',
        message: message
      });
      setTimeout(() => setAlertInfo(null), 5000);

    } catch (error) {
      console.error(' Erro inesperado:', error);
      setAlertInfo({
        show: true,
        type: 'error',
        title: 'Erro ao salvar',
        message: error instanceof Error ? error.message : 'Erro desconhecido ao salvar o checkout'
      });
      setTimeout(() => setAlertInfo(null), 5000);
    } finally {
      setIsSaving(false);
    }
  };

  const handleExport = () => {
    const data = exportDocument();
    navigator.clipboard.writeText(data).then(() => {
      setAlertInfo({
        show: true,
        type: 'success',
        title: 'Sucesso!',
        message: 'Dados copiados para a área de transferência!',
      });
      setTimeout(() => setAlertInfo(null), 5000);
    }).catch(() => {
      // Fallback: mostrar modal com dados
      setShowExportModal(true);
    });
  };

  const handleImport = () => {
    setImportText('');
    setIsImportModalOpen(true);
  };

  const handleConfirmImport = () => {
    if (!importText.trim()) return;

    const result = importDocument(importText.trim());
    if (result.success) {
      setIsImportModalOpen(false);
      setAlertInfo({
        show: true,
        type: 'success',
        title: 'Sucesso!',
        message: 'Checkout importado com sucesso!',
      });
    } else {
      setAlertInfo({
        show: true,
        type: 'error',
        title: 'Erro ao importar',
        message: result.error || 'JSON inválido. Verifique os dados e tente novamente.',
      });
    }
    setTimeout(() => setAlertInfo(null), 5000);
  };

  {/*const handlePreview = () => {
    // Implementar preview em nova aba
    const previewData = exportDocument();
    const newWindow = window.open('', '_blank');
    if (newWindow) {
      newWindow.document.write(`
        <html>
          <head><title>Preview - ${checkoutTitle}</title></head>
          <body>
            <h1>Preview do Checkout: ${checkoutTitle}</h1>
            <p>Funcionalidade de preview será implementada aqui.</p>
            <pre style="background: #f5f5f5; padding: 20px; border-radius: 8px; overflow: auto;">${previewData}</pre>
          </body>
        </html>
      `);
    }
  };
  */}

  return (
    <div className="flex items-center justify-between sticky top-0 z-30 py-3 px-6 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
      <div className="flex items-center gap-4">
        <input
          type="text"
          value={checkoutTitle}
          onChange={handleTitleChange}
          className="text-lg font-medium bg-white border border-gray-300 rounded-md px-3 py-2 text-gray-900 dark:bg-gray-800 dark:border-gray-600 dark:text-white min-w-0 flex-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Nome do Checkout"
        />

        {/* Botões Desktop/Mobile */}
        <div className="flex items-center gap-1">
          <button
            className={`px-3 py-1.5 rounded text-sm flex items-center gap-2 ${ui.breakpoint === 'desktop'
              ? 'bg-gray-900 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
              }`}
            onClick={() => setBreakpoint('desktop')}
          >
            <i className="ri-computer-line text-base mr-1"></i>
            Desktop
          </button>
          <button
            className={`px-3 py-1.5 rounded text-sm flex items-center gap-2 ${ui.breakpoint === 'mobile'
              ? 'bg-gray-900 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
              }`}
            onClick={() => setBreakpoint('mobile')}
          >
            <i className="ri-smartphone-line text-base mr-1"></i>
            Mobile
          </button>

          {/* Botão Copiar do Desktop - só aparece no mobile */}
          {ui.breakpoint === 'mobile' && (
            <button
              className="px-3 py-1.5 rounded text-sm flex items-center gap-2 bg-blue-100 text-blue-700 hover:bg-blue-200 border border-blue-300"
              onClick={() => setShowCopyModal(true)}
              title="Copiar componentes do desktop para mobile (topo→topo, lateral→footer)"
            >
              <i className="ri-file-copy-line text-base"></i>
              Copiar do desktop
            </button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3">
        {/* Indicador de status */}
        {hasUnsavedChanges ? (
          <div className="flex items-center text-yellow-600 text-sm">
            <i className="ri-time-line text-base mr-1"></i>
            Existem alterações não salvas
          </div>
        ) : (
          <div className="flex items-center text-green-600 text-sm">
            <i className="ri-check-line text-base mr-1"></i>
            Tudo certo!
          </div>
        )}

        <div className="flex items-center gap-2">
          <button
            className="px-3 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
            onClick={handleExport}
            title="Exportar checkout"
          >
            <i className="ri-upload-2-line text-base mr-1"></i>
            Exportar
          </button>
          <button
            className="px-3 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
            onClick={handleImport}
            title="Importar checkout"
          >
            <i className="ri-download-2-line text-base mr-1"></i>
            Importar
          </button>
          {/* <button
            className="px-3 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
            onClick={handlePreview}
            title="Visualizar checkout"
          >
            <i className="ri-eye-line text-base mr-1"></i>
            Preview
          </button> */}
          <button
            className={`px-4 py-2 text-white text-sm rounded-md ${isSaving ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
            onClick={() => handleSave(checkoutId)}
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <i className="ri-loader-4-line animate-spin text-base mr-2"></i>
                Salvando...
              </>
            ) : (
              'Salvar checkout'
            )}
          </button>
        </div>
      </div>

      {/* Modal de Importação */}
      {isImportModalOpen && (
        <Modal isOpen={isImportModalOpen} onClose={() => setIsImportModalOpen(false)} title="Importar checkout">
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Cole abaixo os dados exportados do checkout para importar a configuração.
            </p>
            <textarea
              className="w-full h-48 p-3 text-xs font-mono border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={importText}
              onChange={(e) => setImportText(e.target.value)}
              placeholder="Cole aqui o JSON exportado..."
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
                onClick={() => setIsImportModalOpen(false)}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="px-3 py-1.5 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                onClick={handleConfirmImport}
                disabled={!importText.trim()}
              >
                Importar
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Modal de Exportação */}
      {showExportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium">Exportar Checkout</h3>
              <button onClick={() => setShowExportModal(false)} className="text-gray-500 hover:text-gray-700">
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            <p className="text-gray-600 mb-4">Copie os dados abaixo para fazer backup ou importar em outro local:</p>
            <textarea
              className="w-full h-64 p-4 border border-gray-300 rounded-md font-mono text-sm"
              value={exportDocument()}
              readOnly
              onClick={(e) => (e.target as HTMLTextAreaElement).select()}
            />
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => setShowExportModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Fechar
              </button>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(exportDocument());
                  setAlertInfo({
                    show: true,
                    type: 'success',
                    title: 'Sucesso!',
                    message: 'Dados copiados para a área de transferência!'
                  });
                  setTimeout(() => setAlertInfo(null), 5000);
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Copiar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmação - Copiar do Desktop */}
      <Modal
        isOpen={showCopyModal}
        onClose={() => setShowCopyModal(false)}
        size="sm"
        onConfirm={async () => {
          let copiedItems = 0;

          // Criar uma cópia do documento para modificar
          const updatedDoc = JSON.parse(JSON.stringify(state.document));

          // PASSO 1: GARANTIR QUE AS ESTRUTURAS MOBILE EXISTAM
          if (!updatedDoc.nodes['mobile-initial-row']) {
            updatedDoc.nodes['mobile-initial-row'] = {
              id: 'mobile-initial-row',
              parentId: null,
              type: 'row',
              props: { gap: 12, marginY: 12 },
              children: ['mobile-initial-col'],
            };
            updatedDoc.nodes['mobile-initial-col'] = {
              id: 'mobile-initial-col',
              parentId: 'mobile-initial-row',
              type: 'column',
              props: { widthByBp: { mobile: 12 } },
              children: [],
            };
            if (!updatedDoc.mobileRoot) updatedDoc.mobileRoot = [];
            if (!updatedDoc.mobileRoot.includes('mobile-initial-row')) {
              updatedDoc.mobileRoot.push('mobile-initial-row');
            }
          }

          if (!updatedDoc.nodes['mobile-footer-row']) {
            updatedDoc.nodes['mobile-footer-row'] = {
              id: 'mobile-footer-row',
              parentId: null,
              type: 'row',
              props: { gap: 12, marginY: 12 },
              children: ['mobile-footer-col'],
            };
            updatedDoc.nodes['mobile-footer-col'] = {
              id: 'mobile-footer-col',
              parentId: 'mobile-footer-row',
              type: 'column',
              props: { widthByBp: { mobile: 12 } },
              children: [],
            };
            if (!updatedDoc.mobileRoot) updatedDoc.mobileRoot = [];
            if (!updatedDoc.mobileRoot.includes('mobile-footer-row')) {
              updatedDoc.mobileRoot.push('mobile-footer-row');
            }
          }

          replaceDocument(updatedDoc);

          // PASSO 2: LIMPAR COMPONENTES DO MOBILE
          const mobileInitialCol = state.document.nodes['mobile-initial-col'];
          const mobileFooterCol = state.document.nodes['mobile-footer-col'];

          if (mobileInitialCol && mobileInitialCol.type === 'column' && mobileInitialCol.children) {
            [...mobileInitialCol.children].forEach((compId: string) => deleteNode(compId));
          }

          if (mobileFooterCol && mobileFooterCol.type === 'column' && mobileFooterCol.children) {
            [...mobileFooterCol.children].forEach((compId: string) => deleteNode(compId));
          }

          // PASSO 3: COPIAR DO DESKTOP
          const desktopRoot = state.document.root || [];
          const desktopTopComponents: any[] = [];

          desktopRoot.forEach((rowId: string) => {
            const row = state.document.nodes[rowId];
            if (row && row.type === 'row' && row.children) {
              row.children.forEach((colId: string) => {
                const col = state.document.nodes[colId];
                if (col && col.type === 'column' && col.children) {
                  col.children.forEach((compId: string) => {
                    const comp = state.document.nodes[compId];
                    if (comp && comp.type === 'component') {
                      desktopTopComponents.push({
                        type: comp.compType,
                        props: { ...comp.props }
                      });
                    }
                  });
                }
              });
            }
          });

          desktopTopComponents.forEach((comp, index) => {
            addComponent('mobile-initial-col', comp.type, index, comp.props);
            copiedItems++;
          });

          const desktopSideCol = state.document.nodes['side-area-col'];
          if (desktopSideCol && desktopSideCol.type === 'column' && desktopSideCol.children) {
            desktopSideCol.children.forEach((compId: string, index: number) => {
              const comp = state.document.nodes[compId];
              if (comp && comp.type === 'component') {
                addComponent('mobile-footer-col', comp.compType, index, { ...comp.props });
                copiedItems++;
              }
            });
          }

          if (copiedItems > 0) {
            setAlertInfo({
              show: true,
              type: 'success',
              title: 'Sucesso!',
              message: `Copiado com sucesso`
            });
            setTimeout(() => setAlertInfo(null), 5000);
          } else {
            setAlertInfo({
              show: true,
              type: 'warning',
              title: 'Atenção',
              message: 'Não há componentes no desktop para copiar'
            });
            setTimeout(() => setAlertInfo(null), 5000);
          }
          setShowCopyModal(false);
        }}
        confirmText="Copiar"
        cancelText="Cancelar"
        confirmButtonClass="bg-blue-500 hover:bg-blue-600 text-white"
      >
        <div className="flex flex-col items-center text-center py-4">
          <i className="ri-file-copy-line text-6xl text-blue-500 mb-4"></i>
          <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
            Copiar do Desktop?
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 max-w-sm">
            Todos os componentes do mobile serão substituídos pelos componentes do desktop.
          </p>
        </div>
      </Modal>

      {/* Alerta de sucesso/erro - posicionado no canto inferior direito */}
      {
        alertInfo && alertInfo.show && (
          <div className="fixed bottom-4 right-4 z-50 max-w-md">
            <Alert
              variant={alertInfo.type}
              title={alertInfo.title}
              message={alertInfo.message}
            />
          </div>
        )
      }
    </div>
  );
};

interface LeftSidebarProps {
  globalSettings: {
    fontFamily: string;
    backgroundColor: string;
    backgroundImage: string;
    fixedBackground: boolean;
    repeatBackground: boolean;
    expandBackground: boolean;
  };
  setGlobalSettings: React.Dispatch<React.SetStateAction<{
    fontFamily: string;
    backgroundColor: string;
    backgroundImage: string;
    fixedBackground: boolean;
    repeatBackground: boolean;
    expandBackground: boolean;
  }>>;
}

const LeftSidebar: React.FC<LeftSidebarProps> = ({ globalSettings, setGlobalSettings }) => {
  const [tab, setTab] = React.useState<'components' | 'rows' | 'settings'>('components');

  // Escutar o evento personalizado para mudar para a aba 'rows'
  React.useEffect(() => {

    const handleShowLinesTab = (event: CustomEvent) => {
      console.log('LeftSidebar: Evento recebido checkout-builder:show-lines-tab', event.detail);
      console.log('LeftSidebar: Mudando para aba rows');
      setTab('rows');
    };

    // Adicionar o listener de evento
    window.addEventListener('checkout-builder:show-lines-tab', handleShowLinesTab as EventListener);

    // Remover o listener quando o componente for desmontado
    return () => {
      window.removeEventListener('checkout-builder:show-lines-tab', handleShowLinesTab as EventListener);
    };
  }, []);
  const primary = [
    {
      type: 'texto',
      label: 'Texto',
      icon: <i className="ri-text text-lg text-gray-600"></i>,
    },
    {
      type: 'imagem',
      label: 'Imagem',
      icon: <i className="ri-image-line text-lg text-gray-600"></i>,
    },
    {
      type: 'cronometro',
      label: 'Cronômetro',
      icon: <i className="ri-timer-line text-lg text-gray-600"></i>,
    },
  ];
  const others = [
    { label: 'Vantagens' },
    { label: 'Selo' },
    { label: 'Header' },
    { label: 'Lista' },
    { label: 'Depoimento' },
    { label: 'Vídeo' },
    { label: 'Facebook' },
  ];
  return (
    <div className="bg-white dark:bg-gray-900 rounded-lg shadow p-4 min-h-[200px]">
      <div className="flex items-center gap-2 mb-3">
        <button onClick={() => setTab('components')} className={`px-3 py-1.5 text-xs rounded ${tab === 'components' ? 'bg-gray-900 text-white' : 'border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200'}`}>Painel</button>
        <button onClick={() => setTab('rows')} className={`px-3 py-1.5 text-xs rounded ${tab === 'rows' ? 'bg-gray-900 text-white' : 'border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200'}`}>Linhas</button>
        <button onClick={() => setTab('settings')} className={`px-3 py-1.5 text-xs rounded ${tab === 'settings' ? 'bg-gray-900 text-white' : 'border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200'}`}>Configurações</button>
      </div>
      {tab === 'components' && (
        <>
          <h3 className="text-[11px] font-semibold uppercase mb-3 text-gray-500 dark:text-gray-400 tracking-wide">Componentes</h3>
          <Droppable droppableId="palette" direction="vertical">
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                <div className="grid grid-cols-2 gap-3">
                  {primary.map((item, index) => (
                    <Draggable draggableId={`palette:${item.type}`} index={index} key={item.type}>
                      {(dragProvided) => (
                        <div
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          {...dragProvided.dragHandleProps}
                          className="border border-gray-200 dark:border-gray-700 rounded-md p-4 flex flex-col items-center justify-center hover:bg-gray-50 dark:hover:bg-gray-900 cursor-grab select-none bg-white"
                        >
                          <div className="mb-2">{item.icon}</div>
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-200">{item.label}</span>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {others.map((o) => (
                    <div
                      key={o.label}
                      className="border border-gray-200 dark:border-gray-700 rounded-md p-4 flex flex-col items-center justify-center bg-white opacity-60 cursor-not-allowed select-none"
                      aria-disabled
                    >
                      <div className="mb-2">
                        <i className="ri-add-circle-line text-lg text-gray-400"></i>
                      </div>
                      <span className="text-sm font-medium text-gray-500">{o.label}</span>
                    </div>
                  ))}
                </div>
                {provided.placeholder}
              </div>
            )}
          </Droppable>
          <div className="mt-6">
            <h4 className="text:[11px] font-semibold uppercase mb-3 text-gray-500 dark:text-gray-400 tracking-wide">Componentes Extra</h4>
            <div className="grid grid-cols-2 gap-3">
              {['Exit Popup', 'Chat'].map((label) => (
                <div key={label} className="border border-gray-200 dark:border-gray-700 rounded-md p-4 flex flex-col items-center justify-center bg-white select-none">
                  <div className="mb-2">
                    <i className="ri-window-line text-lg text-gray-400"></i>
                  </div>
                  <span className="text-sm font-medium text-gray-600">{label}</span>
                  <div className="mt-2">
                    <label className="inline-flex items-center cursor-not-allowed opacity-60">
                      <span className="mr-2 text-[10px] text-gray-500">Off</span>
                      <input type="checkbox" disabled className="sr-only" />
                      <span className="w-9 h-5 bg-gray-200 rounded-full relative">
                        <span className="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow" />
                      </span>
                    </label>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
      {tab === 'rows' && (
        <div>
          <h3 className="text-[11px] font-semibold uppercase mb-3 text-gray-500 dark:text-gray-400 tracking-wide">Presets de Linhas</h3>
          {/* Make presets draggable too */}
          <Droppable droppableId="row-palette" direction="vertical" type="rows">
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps} className="grid grid-cols-2 gap-3">
                {[
                  { label: '1 Col (12)', layout: [12] },
                  { label: '2 Col (6/6)', layout: [6, 6] },
                  { label: '2 Col (4/8)', layout: [4, 8] },
                  { label: '2 Col (8/4)', layout: [8, 4] },
                  { label: '3 Col (4/4/4)', layout: [4, 4, 4] },
                  { label: '4 Col (3/3/3/3)', layout: [3, 3, 3, 3] },
                ].map((p, index) => {
                  const id = `rowpreset:${p.layout.join('-')}`;
                  return (
                    <Draggable draggableId={id} index={index} key={id}>
                      {(dragProvided) => (
                        <div
                          ref={dragProvided.innerRef as any}
                          {...dragProvided.draggableProps}
                          {...dragProvided.dragHandleProps}
                          className="border border-gray-200 dark:border-gray-700 rounded-md p-3 hover:bg-gray-50 dark:hover:bg-gray-900 text-left bg-white cursor-grab select-none"
                        >
                          <div className="text-xs font-medium text-gray-700 dark:text-gray-200 mb-2">{p.label}</div>
                          <div className="flex gap-1">
                            {p.layout.map((w, i) => (
                              <div key={i} className="h-2 bg-gray-300 dark:bg-gray-700 rounded" style={{ width: `${(w / 12) * 100}%` }} />
                            ))}
                          </div>
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </div>
      )}
      {tab === 'settings' && (
        <div className="space-y-4">
          <h3 className="text-[11px] font-semibold uppercase mb-3 text-gray-500 dark:text-gray-400 tracking-wide">Configurações</h3>

          <div className="space-y-4">
            {/* Font Family */}
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Font Family</label>
              <select
                value={globalSettings.fontFamily}
                onChange={(e) => setGlobalSettings(prev => ({ ...prev, fontFamily: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              >
                <option value="Open Sans">Open Sans</option>
                <option value="Inter">Inter</option>
                <option value="Roboto">Roboto</option>
                <option value="Poppins">Poppins</option>
                <option value="Montserrat">Montserrat</option>
                <option value="Lato">Lato</option>
              </select>
            </div>

            {/* Cor de fundo */}
            <ColorPicker
              color={globalSettings.backgroundColor}
              onChange={(color) => setGlobalSettings(prev => ({ ...prev, backgroundColor: color }))}
              label="Cor de fundo"
            />

            {/* Imagem de fundo */}
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Imagem de fundo</label>

              {!globalSettings.backgroundImage ? (
                <div
                  className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-gray-400 transition-colors cursor-pointer"
                  onClick={() => {
                    const input = document.getElementById('background-image-input') as HTMLInputElement;
                    input?.click();
                  }}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    const file = e.dataTransfer.files?.[0];
                    if (file) {
                      const reader = new FileReader();
                      reader.onload = (e) => setGlobalSettings(prev => ({ ...prev, backgroundImage: e.target?.result as string || '' }));
                      reader.readAsDataURL(file);
                    }
                  }}
                >
                  <div className="text-gray-400 dark:text-gray-500 mb-2">
                    <i className="ri-add-line text-3xl"></i>
                  </div>
                  <p className="text-blue-600 text-sm font-medium cursor-pointer">Selecione do computador</p>
                  <p className="text-gray-500 text-xs">ou arraste aqui</p>
                  <p className="text-gray-400 text-xs mt-1">PNG, JPG até 10 MB</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Preview da imagem */}
                  <div className="relative rounded-lg overflow-hidden border border-gray-200">
                    <img
                      src={globalSettings.backgroundImage}
                      alt="Preview de fundo"
                      className="w-full h-32 object-cover"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-40 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <button
                        onClick={() => {
                          const input = document.getElementById('background-image-input') as HTMLInputElement;
                          input?.click();
                        }}
                        className="px-2 py-1 bg-white text-gray-700 rounded text-xs font-medium hover:bg-gray-100"
                      >
                        Trocar
                      </button>
                      <button
                        onClick={() => setGlobalSettings(prev => ({ ...prev, backgroundImage: '' }))}
                        className="px-2 py-1 bg-red-600 text-white rounded text-xs font-medium hover:bg-red-700"
                      >
                        Remover
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <input
                id="background-image-input"
                type="file"
                accept="image/png,image/jpeg,image/jpg"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    // Validação de tamanho (10MB)
                    if (file.size > 10 * 1024 * 1024) {
                      alert('Arquivo muito grande. Máximo 10MB.');
                      return;
                    }
                    // Validação de tipo
                    if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
                      alert('Formato inválido. Use PNG ou JPG.');
                      return;
                    }
                    const reader = new FileReader();
                    reader.onload = (e) => setGlobalSettings(prev => ({ ...prev, backgroundImage: e.target?.result as string || '' }));
                    reader.readAsDataURL(file);
                  }
                }}
              />
            </div>

            {/* Configurações da imagem de fundo */}
            {globalSettings.backgroundImage && (
              <div className="space-y-3 pt-2 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Fixar imagem ao descer a tela</label>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={globalSettings.fixedBackground}
                      onChange={(e) => setGlobalSettings(prev => ({ ...prev, fixedBackground: e.target.checked }))}
                      className="sr-only"
                    />
                    <div className={`w-11 h-6 rounded-full transition-colors ${globalSettings.fixedBackground ? 'bg-blue-600' : 'bg-gray-200'
                      }`}>
                      <div className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform ${globalSettings.fixedBackground ? 'translate-x-5' : 'translate-x-0'
                        } mt-0.5 ml-0.5`} />
                    </div>
                  </label>
                </div>

                <div className="flex items-center justify-between">
                  <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Repetir imagem de fundo</label>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={globalSettings.repeatBackground}
                      onChange={(e) => setGlobalSettings(prev => ({ ...prev, repeatBackground: e.target.checked }))}
                      className="sr-only"
                    />
                    <div className={`w-11 h-6 rounded-full transition-colors ${globalSettings.repeatBackground ? 'bg-blue-600' : 'bg-gray-200'
                      }`}>
                      <div className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform ${globalSettings.repeatBackground ? 'translate-x-5' : 'translate-x-0'
                        } mt-0.5 ml-0.5`} />
                    </div>
                  </label>
                </div>

                <div className="flex items-center justify-between">
                  <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Expandir / cobrir fundo</label>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={globalSettings.expandBackground}
                      onChange={(e) => setGlobalSettings(prev => ({ ...prev, expandBackground: e.target.checked }))}
                      className="sr-only"
                    />
                    <div className={`w-11 h-6 rounded-full transition-colors ${globalSettings.expandBackground ? 'bg-blue-600' : 'bg-gray-200'
                      }`}>
                      <div className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform ${globalSettings.expandBackground ? 'translate-x-5' : 'translate-x-0'
                        } mt-0.5 ml-0.5`} />
                    </div>
                  </label>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
  ;

// Panel on the left: palette always visible; Inspector slides in over it from left when something is selected
interface LeftPanelProps {
  globalSettings: {
    fontFamily: string;
    backgroundColor: string;
    backgroundImage: string;
    fixedBackground: boolean;
    repeatBackground: boolean;
    expandBackground: boolean;
  };
  setGlobalSettings: React.Dispatch<React.SetStateAction<{
    fontFamily: string;
    backgroundColor: string;
    backgroundImage: string;
    fixedBackground: boolean;
    repeatBackground: boolean;
    expandBackground: boolean;
  }>>;
}

const LeftPanel: React.FC<LeftPanelProps> = ({ globalSettings, setGlobalSettings }) => {
  const { state } = useBuilderStore();
  const { selectionId } = state;
  return (
    <div className="relative h-full">
      {/* Palette base layer */}
      <div className={`h-full transition-opacity duration-200 ${selectionId ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
        <div className="h-full overflow-y-auto p-4">
          <LeftSidebar globalSettings={globalSettings} setGlobalSettings={setGlobalSettings} />
        </div>
      </div>
      {/* Inspector overlay - slides in from left */}
      <div
        className={`absolute inset-0 z-20 transform transition-transform duration-300 ease-out ${selectionId ? 'translate-x-0' : '-translate-x-full'}`}
        style={{ pointerEvents: selectionId ? 'auto' : 'none' }}
      >
        <div className="h-full w-full overflow-y-auto bg-white dark:bg-gray-900 shadow-lg">
          <Inspector />
        </div>
      </div>
    </div>
  );
};

interface BuilderShellInnerProps {
  checkoutId?: string;
  productId?: string;
  offerId?: string;
  checkoutName?: string;
  initialConfig?: any;
}

const BuilderShellInner: React.FC<BuilderShellInnerProps> = ({ checkoutId, productId, offerId, checkoutName, initialConfig }) => {
  const { state, undo, redo, addComponent, insertRowWithComponentAt, insertRowWithLayoutAt, moveRowToIndex, moveComponentWithinColumn, moveComponentBetweenColumns, replaceDocument } = useBuilderStore();

  // Estado global das configurações visuais
  const [globalSettings, setGlobalSettings] = React.useState({
    fontFamily: 'Open Sans',
    backgroundColor: '#ffffff',
    backgroundImage: '',
    fixedBackground: false,
    repeatBackground: false,
    expandBackground: false
  });

  // Carregar configurações iniciais apenas uma vez
  const [configLoaded, setConfigLoaded] = React.useState(false);

  React.useEffect(() => {
    if (initialConfig && !configLoaded) {
      try {
        // Carregar AMBOS os documentos (desktop E mobile) de uma vez
        const desktopConfig = initialConfig.desktop;
        const mobileConfig = initialConfig.mobile;

        if (desktopConfig && desktopConfig.document) {
          // Validar e limpar o documento desktop
          const cleanedDesktopDoc = { ...desktopConfig.document };

          if (cleanedDesktopDoc.nodes) {
            const validNodeIds = new Set(Object.keys(cleanedDesktopDoc.nodes));

            Object.values(cleanedDesktopDoc.nodes).forEach((node: any) => {
              if (node.type === 'column' && node.children) {
                node.children = node.children.filter((childId: string) => validNodeIds.has(childId));
              }
              if (node.type === 'row' && node.children) {
                node.children = node.children.filter((childId: string) => validNodeIds.has(childId));
              }
            });
          }

          // Adicionar nodes do mobile ao documento desktop
          if (mobileConfig && mobileConfig.document && mobileConfig.document.nodes) {
            cleanedDesktopDoc.nodes = {
              ...cleanedDesktopDoc.nodes,
              ...mobileConfig.document.nodes
            };

            // Adicionar mobileRoot se existir
            if (mobileConfig.document.mobileRoot) {
              (cleanedDesktopDoc as any).mobileRoot = mobileConfig.document.mobileRoot;
            }
          }

          // Carregar o documento unificado
          replaceDocument(cleanedDesktopDoc);

          // Carregar globalSettings do desktop
          if (desktopConfig.globalSettings) {
            setGlobalSettings({
              fontFamily: desktopConfig.globalSettings.fontFamily || 'Open Sans',
              backgroundColor: desktopConfig.globalSettings.backgroundColor || '#ffffff',
              backgroundImage: desktopConfig.globalSettings.backgroundImage || '',
              fixedBackground: desktopConfig.globalSettings.fixedBackground || false,
              repeatBackground: desktopConfig.globalSettings.repeatBackground || false,
              expandBackground: desktopConfig.globalSettings.expandBackground || false
            });
          }
        }

        setConfigLoaded(true);
      } catch (error) {
        console.error('Erro ao carregar configurações:', error);
        setConfigLoaded(true);
      }
    }
  }, [initialConfig, configLoaded, replaceDocument]);

  // Adicionar suporte a teclas de atalho
  React.useEffect(() => {
    const handleKeydown = (event: KeyboardEvent) => {
      // Ctrl+Z ou Cmd+Z para desfazer
      if ((event.ctrlKey || event.metaKey) && event.key === 'z' && !event.shiftKey) {
        event.preventDefault();
        undo();
        return;
      }
      // Ctrl+Y ou Cmd+Shift+Z para refazer
      if (((event.ctrlKey || event.metaKey) && event.key === 'y') ||
        ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === 'Z')) {
        event.preventDefault();
        redo();
        return;
      }
      // Escape para deselecionar
      if (event.key === 'Escape') {
        // Implementar deselecionar se necessário
      }
    };

    document.addEventListener('keydown', handleKeydown);
    return () => document.removeEventListener('keydown', handleKeydown);
  }, [undo, redo]);

  function onDragEnd(result: DropResult) {
    const { source, destination, draggableId } = result;
    if (!destination) return;
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;

    // From palette to existing column (including checkout side column)
    if (source.droppableId === 'palette' && destination.droppableId.startsWith('col:')) {
      const colId = destination.droppableId.slice(4);
      const componentType = draggableId.replace('palette:', '');
      const registry = REGISTRY.find((r) => r.type === componentType);
      addComponent(colId, componentType, destination.index, registry?.defaultProps || {});
      return;
    }

    // From palette to zone:0 (main drop zone) or mobile-zone:0
    if (source.droppableId === 'palette' && (destination.droppableId === 'zone:0' || destination.droppableId === 'mobile-zone:0')) {
      const componentType = draggableId.replace('palette:', '');
      const registry = REGISTRY.find((r) => r.type === componentType);
      const isMobileZone = destination.droppableId === 'mobile-zone:0';

      // Create a new row with the component in the appropriate zone
      // TODO: Implementar suporte completo ao mobileRoot na store
      insertRowWithComponentAt([12], destination.index, componentType, registry?.defaultProps || {});

      // Por enquanto, vamos só mostrar que detectou a zona mobile
      if (isMobileZone) {
        console.log('Componente adicionado na zona mobile:', componentType);
      }
      return;
    }

    // Helper function to calculate zone base indices (eliminates duplication)
    const counts = ((state.document.meta as any)?.zoneCounts) || { top: 0, side: 0, bottom: 0 };
    const getZoneBase = (zoneIndex: number): number => {
      switch (zoneIndex) {
        case 0: return 0; // top zone
        case 1: return counts.top; // side zone
        case 2: return counts.top + counts.side; // bottom zone
        default: return 0;
      }
    };

    const destZone = destination.droppableId.startsWith('zone:') ? parseInt(destination.droppableId.split(':')[1] || '0', 10) : null;

    // Dragging a preset from row-palette into a zone
    if (source.droppableId === 'row-palette' && destZone !== null) {
      if (draggableId.startsWith('rowpreset:')) {
        const presetLayout = draggableId.replace('rowpreset:', '').split('-').map(Number);
        const insertAt = getZoneBase(destZone) + destination.index;
        insertRowWithLayoutAt(presetLayout, insertAt);
        return;
      }
    }

    // Moving existing rows between zones
    if (source.droppableId.startsWith('zone:') && destZone !== null) {
      const srcZone = parseInt(source.droppableId.split(':')[1] || '0', 10);
      const rowId = draggableId.replace('row:', '');

      // Calculate absolute indices
      const fromAbs = getZoneBase(srcZone) + source.index;
      let toAbs = getZoneBase(destZone) + destination.index;

      // Adjust index when moving within same zone and moving down
      if (srcZone === destZone && fromAbs < toAbs) {
        toAbs -= 1;
      }

      moveRowToIndex(rowId, toAbs);
      return;
    }

    // Move components within/between columns
    if (source.droppableId.startsWith('col:') && destination.droppableId.startsWith('col:')) {
      const fromCol = source.droppableId.slice(4);
      const toCol = destination.droppableId.slice(4);

      if (fromCol === toCol) {
        moveComponentWithinColumn(fromCol, source.index, destination.index);
      } else {
        moveComponentBetweenColumns(fromCol, toCol, source.index, destination.index);
      }
      return;
    }
  }

  return (
    <div className="h-screen flex flex-col bg-white dark:bg-gray-900">
      <PageMeta title="Personalizar Checkout | Sellyo" description="Checkout Builder" />
      <Toolbar checkoutId={checkoutId} productId={productId} offerId={offerId} checkoutName={checkoutName} globalSettings={globalSettings} />
      <DragDropContext onDragEnd={onDragEnd}>
        <div className="flex-1 flex overflow-hidden">
          {/* Left panel - fixed width, no scroll */}
          <div className="w-80 flex-shrink-0 h-full overflow-hidden border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
            <LeftPanel globalSettings={globalSettings} setGlobalSettings={setGlobalSettings} />
          </div>
          {/* Canvas - takes remaining space, scrollable */}
          <div
            className="flex-1 overflow-y-auto"
            style={{
              backgroundColor: globalSettings.backgroundColor,
              backgroundImage: globalSettings.backgroundImage ? `url(${globalSettings.backgroundImage})` : 'none',
              backgroundAttachment: globalSettings.fixedBackground ? 'fixed' : 'scroll',
              backgroundRepeat: globalSettings.repeatBackground ? 'repeat' : 'no-repeat',
              backgroundSize: globalSettings.expandBackground ? 'cover' : 'auto'
            }}
          >
            <div className="flex justify-center py-8">
              <div className={`w-full px-4 ${state.ui?.breakpoint === 'mobile' ? 'max-w-sm' : 'max-w-6xl'}`}>
                <LegacyStage globalSettings={globalSettings} />
              </div>
            </div>
          </div>
        </div>
      </DragDropContext>
    </div>
  );
};

interface BuilderShellProps {
  checkoutId?: string;
  productId?: string;
  offerId?: string;
  checkoutName?: string;
  initialConfig?: any; // TODO: Implementar carregamento do initialConfig no contexto
}

const BuilderShell: React.FC<BuilderShellProps> = ({ checkoutId, productId, offerId, checkoutName, initialConfig }) => {
  return (
    <CheckoutBuilderErrorBoundary>
      <BuilderProvider>
        <BuilderShellInner
          checkoutId={checkoutId}
          productId={productId}
          offerId={offerId}
          checkoutName={checkoutName}
          initialConfig={initialConfig}
        />
      </BuilderProvider>
    </CheckoutBuilderErrorBoundary>
  );
};

export default BuilderShell;
